/**
 * Default formatters for LineChart. All are overridable via
 * `yAxisFormatter` / `xAxisFormatter` / `tooltipFormatter` props.
 */

// Hoisted once — constructing an Intl formatter per call is measurably
// expensive when there's one call per X tick per render plus one per
// tooltip update.
const AXIS_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const CURRENCY_FORMATTER_INT = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const CURRENCY_FORMATTER_DEC = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Abbreviates to `$60K` / `$1.2M` style — used on the Y axis. */
export function formatCurrencyAbbreviated(value) {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${sign}$${trimDecimal(abs / 1_000_000)}M`;
  if (abs >= 1_000) return `${sign}$${trimDecimal(abs / 1_000)}K`;
  return `${sign}$${trimDecimal(abs)}`;
}

/** Full currency with thousands separators — used in the tooltip. */
export function formatCurrency(value) {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  const formatter = Number.isInteger(abs) ? CURRENCY_FORMATTER_INT : CURRENCY_FORMATTER_DEC;
  return `${sign}$${formatter.format(abs)}`;
}

/** `Feb 8` style short date — used on the X axis and in the tooltip. */
export function formatAxisDate(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return AXIS_DATE_FORMATTER.format(date);
}

/** `date value` — the default tooltip badge text, e.g. `Mar 26 $60,012.10`. */
export function formatTooltipPoint(point) {
  return `${formatAxisDate(point.x)} ${formatCurrency(point.y)}`;
}

function trimDecimal(n) {
  return Number.isInteger(n) ? n : Math.round(n * 10) / 10;
}
